import { useHydrated, useLocation, useParams } from '@tanstack/react-router';
import type { BackOrigin } from './backNavigation';
import { getOverviewTarget, getScrollState } from './backNavigation';

/**
 * Where a webinar page's back link goes, and its text (a translation key): the page it was opened from
 * (`state.backTo`), else `fallback`. Before hydration always `fallback` with upcoming as the overview tab: the server
 * has no history state or sessionStorage, and the first client render must match its HTML.
 */
export const useBackTarget = (fallback: BackOrigin) => {
    const hydrated = useHydrated();
    const backTo = useLocation({ select: (location) => location.state.backTo });
    const { webinarId } = useParams({ strict: false });

    const origin = (hydrated && backTo) || fallback;
    if (origin !== 'overview' && webinarId) {
        // At the position you left it, when it is the page of this webinar you looked at last.
        const state = hydrated ? getScrollState(origin, `/${webinarId}/${origin}`) : undefined;
        return origin === 'content'
            ? ({ to: '/$webinarId/content', params: { webinarId }, state, label: 'Button.BackToContent' } as const)
            : ({ to: '/$webinarId/insights', params: { webinarId }, state, label: 'Button.BackToInsights' } as const);
    }

    const overview = hydrated ? getOverviewTarget() : ({ to: '/organized/$tab', params: { tab: 'upcoming' }, state: undefined } as const);
    return { ...overview, label: overview.params.tab === 'ended' ? 'Button.BackToOrganizedEnded' : 'Button.BackToOrganized' } as const;
};
